import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import EmptySocialTopics from "@/components/content/social-topics/EmptySocialTopics";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Search, Filter, Sparkles, Loader2, ArrowRight, Instagram, Calendar, Brain, Zap, Database, FileText, Check, Trash2, Maximize2, Minimize2, Eye, EyeOff, Image, Video, Copy, Share2, ExternalLink, Users, Clock, X, Play, CheckCircle, CirclePlay, Quote, Camera, BarChart } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { Hash } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ChevronLeft, ChevronRight, Calendar as CalendarIcon, Trophy } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import SocialTopicList from "@/components/content/social-topics/SocialTopicList";

interface SocialTopicMetadata {
  visualDescription: string;
  caption: string;
  type: string;
  audience: string;
  hashtags: string;
  isPosted: boolean;
  postedDate?: string;
  challengePosition?: number;
  isArchived?: boolean;
}

interface SocialTopic {
  id: string;
  visualDescription: string;
  caption: string;
  type: string;
  audience: string;
  hashtags: string;
  isPosted: boolean;
  metadata: SocialTopicMetadata;
  createdAt?: string;
}

// Add this interface to handle the database response
interface DatabaseContent {
  id: string;
  content: string;
  metadata: any;
  created_at: string;
  [key: string]: any;
}

const SocialTopicsPage = () => {
  const { id } = useParams();
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationStep, setGenerationStep] = useState(0);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [selectedTopics, setSelectedTopics] = useState<string[]>([]);
  const [showNewTopicDialog, setShowNewTopicDialog] = useState(false);
  const [isGeneratingContent, setIsGeneratingContent] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [newTopic, setNewTopic] = useState({ 
    visualDescription: '', 
    caption: '',
    type: 'carousel',
    audience: 'marketing-managers',
    hashtags: '#marketing #socialmedia #content'
  });
  const [isCreating, setIsCreating] = useState(false);
  const [isGeneratingMore, setIsGeneratingMore] = useState(false);
  const [showCalendarDialog, setShowCalendarDialog] = useState(false);
  
  // Add state for agent descriptions
  const [agentDescriptions, setAgentDescriptions] = useState({
    research: "Parsing website DOM structure and extracting semantic data...",
    creative: "Analyzing information architecture and content hierarchy...",
    writing: "Processing natural language patterns and keyword density...",
    hashtag: "Evaluating search intent signals and ranking factors..."
  });
  
  // Initialize with empty array to prevent undefined errors
  const [topics, setTopics] = useState<SocialTopic[]>([]);
  const [selectedTopic, setSelectedTopic] = useState<SocialTopic | null>(null);
  const [showTopicDetailDialog, setShowTopicDetailDialog] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [expandedPost, setExpandedPost] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFetchingTopics, setIsFetchingTopics] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());

  // Helper function to get the correct day number display
  const getDayNumber = (topic: SocialTopic | null, defaultDay: number) => {
    if (topic?.isPosted && topic?.metadata?.challengePosition) {
      return topic.metadata.challengePosition;
    }
    return defaultDay;
  };

  // Use the useQuery hook with proper error handling
  const { data: topicsData, isLoading: isQueryLoading, error: topicsError } = useQuery({
    queryKey: ['social-topics', id],
    queryFn: async () => {
      try {
        setIsLoading(true);
        
        if (!id) {
          throw new Error("Project ID is required");
        }
        
        // Fetch topics from the database
        const { data, error, status } = await supabase
          .from('generated_content')
          .select('*')
          .eq('project_id', id)
          .eq('platform', 'instagram');
        
        if (error) {
          if (status === 401) {
            throw new Error("Authentication error. Please sign in again.");
          } else if (status === 403) {
            throw new Error("You don't have permission to access these topics.");
          } else if (status === 404) {
            throw new Error("No topics found for this project.");
          } else {
            throw new Error(`Database error: ${error.message}`);
          }
        }
        
        if (!data || data.length === 0) {
          setTopics([]);
          setIsLoading(false);
          return [];
        }
        
        // Transform the data with proper type handling
        const transformedData: SocialTopic[] = (data as DatabaseContent[]).map(item => {
          try {
            // Cast metadata to handle it properly
            const meta = item.metadata as any;
            
            return {
              id: item.id,
              visualDescription: meta?.visualDescription || '',
              caption: item.content || '',
              type: meta?.type || 'single-image',
              audience: meta?.audience || 'general-audience',
              hashtags: meta?.hashtags || '',
              isPosted: meta?.isPosted || false,
              metadata: {
                visualDescription: meta?.visualDescription || '',
                caption: item.content || '',
                type: meta?.type || 'single-image',
                audience: meta?.audience || 'general-audience',
                hashtags: meta?.hashtags || '',
                isPosted: meta?.isPosted || false,
                postedDate: meta?.postedDate,
                challengePosition: meta?.challengePosition || 0,
                isArchived: meta?.isArchived || false
              },
              createdAt: item.created_at
            };
          } catch (itemError) {
            console.error('Error transforming topic item:', itemError, item);
            // Return a default object with the ID to avoid breaking the UI
            return {
              id: item.id,
              visualDescription: 'Error loading description',
              caption: 'Error loading content',
              type: 'single-image',
              audience: 'general-audience',
              hashtags: '',
              isPosted: false,
              metadata: {
                visualDescription: 'Error loading description',
                caption: 'Error loading content',
                type: 'single-image',
                audience: 'general-audience',
                hashtags: '',
                isPosted: false,
                challengePosition: 0,
                isArchived: false
              },
              createdAt: item.created_at
            };
          }
        });
        
        // Update the local state
        setTopics(transformedData);
        setIsLoading(false);
        
        return transformedData;
      } catch (error: any) {
        console.error('Error loading social topics:', error);
        setIsLoading(false);
        setTopics([]);
        
        // Show toast with error message
        toast({
          title: "Error loading topics",
          description: error.message || "Failed to load social topics",
          variant: "destructive",
        });
        
        return [];
      }
    },
    enabled: !!id,
    retry: 2, // Retry failed requests up to 2 times
    retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 10000), // Exponential backoff
    staleTime: 5 * 60 * 1000, // Consider data stale after 5 minutes
  });

  // Handle post-query effects
  useEffect(() => {
    if (topicsData) {
      setTopics(topicsData);
    }
    
    // Update loading state based on query loading state
    if (!isQueryLoading && isFetchingTopics) {
      setIsFetchingTopics(false);
    }
  }, [topicsData, isQueryLoading, isFetchingTopics]);

  const generateSocialTopics = async () => {
    if (!user?.id || !id) {
      toast({
        title: "Error",
        description: "Authentication required",
        variant: "destructive",
      });
      return;
    }

    setIsGenerating(true);
    setIsFetchingTopics(true);
    setGenerationStep(0);
    setGenerationProgress(0);

    // Simulate AI agent steps with progress
    const simulateProgress = () => {
      const steps = [
        { step: 0, message: "Initializing AI agents...", duration: 1000 },
        { step: 1, message: "Analyzing website content...", duration: 2000 },
        { step: 2, message: "Identifying target audience segments...", duration: 2000 },
        { step: 3, message: "Extracting key brand messaging...", duration: 1500 },
        { step: 4, message: "Generating visual concepts...", duration: 2500 },
        { step: 5, message: "Crafting engaging captions...", duration: 2000 },
        { step: 6, message: "Optimizing hashtag selection...", duration: 1500 },
        { step: 7, message: "Finalizing Instagram content ideas...", duration: 2000 },
      ];

      let currentStep = 0;
      const totalSteps = steps.length;

      const interval = setInterval(() => {
        if (currentStep < totalSteps) {
          setGenerationStep(steps[currentStep].step);
          setGenerationProgress((currentStep / (totalSteps - 1)) * 100);
          currentStep++;
        } else {
          clearInterval(interval);
        }
      }, 1500);

      // Update agent descriptions periodically
      const descriptionInterval = setInterval(() => {
        setAgentDescriptions(prev => {
          // Research agent descriptions (0-25%)
          if (generationProgress < 25) {
            return {
              ...prev,
              research: [
                "Parsing website DOM structure and extracting semantic data...",
                "Running heuristic analysis on site architecture...",
                "Identifying core business entities and relationships...",
                "Processing structured data and meta information..."
              ][Math.floor(Math.random() * 4)]
            };
          } 
          // Analysis agent descriptions (25-50%)
          else if (generationProgress < 50) {
            return {
              ...prev,
              creative: [
                "Analyzing information architecture and content hierarchy...",
                "Evaluating content-market fit through NLP models...",
                "Processing user interaction patterns and flows...",
                "Mapping semantic relationships between content blocks..."
              ][Math.floor(Math.random() * 4)]
            };
          } 
          // Data Processing agent descriptions (50-75%)
          else if (generationProgress < 75) {
            return {
              ...prev,
              writing: [
                "Processing natural language patterns and keyword density...",
                "Analyzing content readability and complexity metrics...",
                "Extracting key topics through TF-IDF analysis...",
                "Computing semantic similarity across content segments..."
              ][Math.floor(Math.random() * 4)]
            };
          }
          // SEO agent descriptions (75-100%)
          else {
            return {
              ...prev,
              hashtag: [
                "Evaluating search intent signals and ranking factors...",
                "Processing keyword co-occurrence patterns...",
                "Analyzing search volume and competition metrics...",
                "Computing keyword difficulty and opportunity scores..."
              ][Math.floor(Math.random() * 4)]
            };
          }
        });
      }, 3000);

      return { progressInterval: interval, descriptionInterval };
    };

    const { progressInterval, descriptionInterval } = simulateProgress();

    try {
      // First, fetch the latest analysis results for the project
      const { data: analysisData, error: analysisError } = await supabase
        .from('website_crawls')
        .select('analysis_results')
        .eq('project_id', id)
        .eq('status', 'completed')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (analysisError) throw analysisError;

      if (!analysisData?.analysis_results) {
        clearInterval(progressInterval);
        clearInterval(descriptionInterval);
        toast({
          title: "Error",
          description: "No analysis results found. Please analyze your website first.",
          variant: "destructive",
        });
        return;
      }

      // Call the generate-instagram-topics edge function
      const { data: topicsData, error: topicsError } = await supabase.functions.invoke('generate-instagram-topics', {
        body: { 
          projectId: id,
          analysisResults: analysisData.analysis_results
        }
      });

      if (topicsError) throw topicsError;

      // Save the generated topics to the database
      const { error: saveError } = await supabase.from('generated_content').insert(
        topicsData.topics.map((topic: any) => ({
          project_id: id,
          user_id: user.id,
          platform: 'instagram',
          content: topic.caption,
          metadata: {
            visualDescription: topic.visualDescription,
            type: topic.type,
            audience: topic.audience,
            hashtags: topic.hashtags,
            isPosted: false
          }
        }))
      );

      if (saveError) throw saveError;

      // Ensure we show 100% progress
      setGenerationProgress(100);
      setGenerationStep(7);
      
      // Wait a moment to show the completed state
      setTimeout(() => {
        clearInterval(progressInterval);
        clearInterval(descriptionInterval);
        
        // Refetch topics to update the UI
        queryClient.invalidateQueries({ queryKey: ['social-topics'] });

        toast({
          title: "Success",
          description: "Instagram post ideas generated successfully",
        });
      }, 1500);

    } catch (error: any) {
      clearInterval(progressInterval);
      clearInterval(descriptionInterval);
      console.error('Error generating social topics:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to generate social topics",
        variant: "destructive",
      });
    } finally {
      setTimeout(() => {
        setIsGenerating(false);
        setIsFetchingTopics(false);
      }, 1500);
    }
  };

  const handleTopicSelect = (topicId: string) => {
    setSelectedTopics(prev => {
      if (prev.includes(topicId)) {
        return prev.filter(id => id !== topicId);
      } else {
        return [...prev, topicId];
      }
    });
  };

  const togglePosted = async (topicId: string) => {
    try {
      setIsLoading(true);
      
      // Find the topic to update
      const topicToUpdate = topics.find(t => t?.id === topicId);
      if (!topicToUpdate) {
        setIsLoading(false);
        return;
      }

      const newPostedStatus = !topicToUpdate.isPosted;
      
      // Create a local copy of topics to work with
      const currentTopics = [...topics];
      const postedTopics = currentTopics.filter(t => t?.isPosted);
      
      // Calculate the position in the challenge
      let challengePosition = 0;
      
      if (newPostedStatus) {
        // If marking as posted, assign the next position
        challengePosition = postedTopics.length + 1;
        
        // Update in the database
        const { error } = await supabase
          .from('generated_content')
          .update({ 
            metadata: {
              ...topicToUpdate.metadata,
              isPosted: newPostedStatus,
              postedDate: new Date().toISOString(),
              challengePosition
            }
          })
          .eq('id', topicId);

        if (error) throw error;
      } else {
        // If unmarking, we need to update all subsequent positions
        const currentPosition = topicToUpdate.metadata.challengePosition || 0;
        
        // First update the current topic
        const { error: updateError } = await supabase
          .from('generated_content')
          .update({ 
            metadata: {
              ...topicToUpdate.metadata,
              isPosted: false,
              postedDate: null,
              challengePosition: 0
            }
          })
          .eq('id', topicId);
          
        if (updateError) throw updateError;
        
        // Then update all topics with higher positions in parallel
        const topicsToUpdate = currentTopics.filter(topic => 
          topic?.isPosted && 
          topic?.id !== topicId && 
          (topic?.metadata?.challengePosition || 0) > currentPosition
        );
        
        if (topicsToUpdate.length > 0) {
          const updatePromises = topicsToUpdate.map(topic => 
            supabase
              .from('generated_content')
              .update({ 
                metadata: {
                  ...topic.metadata,
                  challengePosition: (topic?.metadata?.challengePosition || 0) - 1
                }
              })
              .eq('id', topic?.id)
          );
          
          const results = await Promise.all(updatePromises);
          const errors = results.filter(result => result.error);
          
          if (errors.length > 0) {
            console.error('Errors updating positions:', errors);
            throw new Error('Some position updates failed');
          }
        }
      }

      // Update local state with a functional update to avoid race conditions
      setTopics(prev => {
        const updatedTopics = prev.map(topic => {
          if (topic?.id === topicId) {
            return { 
              ...topic, 
              isPosted: newPostedStatus,
              metadata: {
                ...topic.metadata,
                isPosted: newPostedStatus,
                postedDate: newPostedStatus ? new Date().toISOString() : null,
                challengePosition: newPostedStatus ? challengePosition : 0
              }
            };
          } else if (
            !newPostedStatus && 
            topic?.isPosted && 
            (topic?.metadata?.challengePosition || 0) > (topicToUpdate.metadata.challengePosition || 0)
          ) {
            return {
              ...topic,
              metadata: {
                ...topic.metadata,
                challengePosition: (topic?.metadata?.challengePosition || 0) - 1
              }
            };
          }
          return topic;
        });
        
        return updatedTopics;
      });

      // Refetch to ensure we have the latest data with updated positions
      await queryClient.invalidateQueries({ queryKey: ['social-topics'] });

      toast({
        title: newPostedStatus ? "Content Posted! Let's go!" : "Moved to posted",
        description: `"${topicToUpdate.caption.substring(0, 30)}..." ${newPostedStatus ? `is now day ${challengePosition} in your challenge` : 'has been moved to posted content'}`,
      });
    } catch (error: any) {
      console.error('Error toggling posted status:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to update posted status",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const deleteTopic = async (topicId: string) => {
    try {
      // Find the topic to check if it's posted
      const topicToDelete = topics.find(t => t.id === topicId);
      
      if (!topicToDelete) {
        toast({
          title: "Error",
          description: "Topic not found",
          variant: "destructive",
        });
        return;
      }
      
      // If it's a posted topic, we need to update the challenge positions of other posts
      if (topicToDelete.isPosted && topicToDelete.metadata.challengePosition) {
        const currentPosition = topicToDelete.metadata.challengePosition;
        
        // Find topics with higher positions
        const topicsToUpdate = topics.filter(topic => 
          topic?.isPosted && 
          topic?.id !== topicId && 
          (topic?.metadata?.challengePosition || 0) > currentPosition
        );
        
        // Update positions in database for all topics with higher positions
        if (topicsToUpdate.length > 0) {
          const updatePromises = topicsToUpdate.map(topic => 
            supabase
              .from('generated_content')
              .update({ 
                metadata: {
                  ...topic.metadata,
                  challengePosition: (topic?.metadata?.challengePosition || 0) - 1
                }
              })
              .eq('id', topic?.id)
          );
          
          await Promise.all(updatePromises);
        }
      }
      
      // Delete from the database
      const { error } = await supabase
        .from('generated_content')
        .delete()
        .eq('id', topicId);

      if (error) throw error;

      // Update local state
      setTopics(prev => prev.filter(topic => topic.id !== topicId));
      setSelectedTopics(prev => prev.filter(id => id !== topicId));

      toast({
        title: "Topic deleted",
        description: "The social topic has been deleted successfully",
      });
    } catch (error: any) {
      console.error('Error deleting topic:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete topic",
        variant: "destructive",
      });
    }
  };

  const handleCreateTopic = () => {
    setShowNewTopicDialog(true);
  };

  const submitNewTopic = async () => {
    if (!user?.id || !id) {
      toast({
        title: "Error",
        description: "Authentication required",
        variant: "destructive",
      });
      return;
    }

    if (!newTopic.visualDescription || !newTopic.caption) {
      toast({
        title: "Missing information",
        description: "Please provide both a visual description and caption",
        variant: "destructive",
      });
      return;
    }

    setIsCreating(true);

    try {
      const metadata = {
        visualDescription: newTopic.visualDescription,
        caption: newTopic.caption,
        type: newTopic.type,
        audience: newTopic.audience,
        hashtags: newTopic.hashtags,
        isPosted: false
      };

      // Save to the database
      const { data, error } = await supabase
        .from('generated_content')
        .insert({
          project_id: id,
          user_id: user.id,
          platform: 'instagram',
          content: newTopic.caption, // Store caption in content for backward compatibility
          metadata
        })
        .select()
        .single();

      if (error) throw error;

      // Add to local state with consistent structure
      const newTopicWithId: SocialTopic = {
        id: data.id,
        visualDescription: metadata.visualDescription,
        caption: metadata.caption,
        type: metadata.type,
        audience: metadata.audience,
        hashtags: metadata.hashtags,
        isPosted: false,
        metadata
      };

      setTopics(prev => [newTopicWithId, ...prev]);
      
      // Reset form
      setNewTopic({ 
        visualDescription: '', 
        caption: '',
        type: 'carousel',
        audience: 'marketing-managers',
        hashtags: '#marketing #socialmedia #content'
      });
      
      setShowNewTopicDialog(false);
      
      toast({
        title: "Topic created",
        description: "Your new social topic has been created successfully",
      });
    } catch (error: any) {
      console.error('Error creating topic:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to create topic",
        variant: "destructive",
      });
    } finally {
      setIsCreating(false);
    }
  };

  const handleGenerateMoreTopics = async () => {
    if (isGeneratingMore) return;
    
    setIsGeneratingMore(true);
    await generateSocialTopics();
    setIsGeneratingMore(false);
  };

  // Get unique content types for display
  const uniqueAudiences = Array.from(new Set(topics.map(topic => topic.audience)));
  const uniqueTypes = Array.from(new Set(topics.map(topic => topic.type)));

  // Add a function to handle post expansion
  const handlePostExpand = (topicId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedPost(expandedPost === topicId ? null : topicId);
  };

  // Add a function to handle opening the topic detail dialog
  const handleTopicClick = (topic: SocialTopic) => {
    // If the topic is already expanded, collapse it
    if (expandedPost === topic.id) {
      setExpandedPost(null);
    } else {
      // Otherwise expand it
      setExpandedPost(topic.id);
      setSelectedTopic(topic);
    }
  };

  // Add this function to prevent click propagation when selecting text
  const preventCardCollapse = (e: React.MouseEvent) => {
    // This prevents the click from bubbling up to the card's onClick handler
    e.stopPropagation();
  };

  // Add a separate function to open the dialog
  const openTopicDialog = (topic: SocialTopic) => {
    setSelectedTopic(topic);
    setShowTopicDetailDialog(true);
  };

  // Filter topics based on posted status
  const filteredTopics = topics.filter(topic => {
    if (!topic) return false;
    
    // Only filter by posted status
    return showArchived ? topic.isPosted : !topic.isPosted;
  });

  const getMonthName = (month: number) => {
    return new Date(2000, month).toLocaleString('default', { month: 'long' });
  };

  const navigateMonth = (direction: 'prev' | 'next') => {
    if (direction === 'prev') {
      if (currentMonth === 0) {
        setCurrentMonth(11);
        setCurrentYear(currentYear - 1);
      } else {
        setCurrentMonth(currentMonth - 1);
      }
    } else {
      if (currentMonth === 11) {
        setCurrentMonth(0);
        setCurrentYear(currentYear + 1);
      } else {
        setCurrentMonth(currentMonth + 1);
      }
    }
  };

  // Improved text selection handling function
  const handleTextInteraction = (e: React.MouseEvent | React.TouchEvent, topicId: string) => {
    // Only prevent propagation if the card is already expanded
    if (expandedPost === topicId) {
      e.stopPropagation();
    }
    // If card is closed, let the click propagate to open it
  };

  // Improved function to handle text selection starting
  const handleSelectionStart = (e: React.MouseEvent, topicId: string) => {
    // Only prevent propagation if the card is already expanded
    if (expandedPost === topicId) {
      // Don't stop propagation of the initial click to allow double/triple clicks to work
      // Only stop propagation if it's a potential drag start (mouse down with movement intent)
      if (e.detail === 1) { // Single click
        // We'll let it propagate
      } else {
        // Double or triple click for word/paragraph selection
        e.stopPropagation();
      }
    }
    // If card is closed, let the click propagate to open it
  };

  // Add a global selection handler for when selection extends beyond the element
  useEffect(() => {
    const handleGlobalMouseUp = () => {
      // If we have an expanded post and selection, ensure the card doesn't collapse
      if (expandedPost && window.getSelection()?.toString()) {
        // Selection exists, prevent collapse on next click
        const handleNextClick = (e: MouseEvent) => {
          // If there's still a selection, prevent the click
          if (window.getSelection()?.toString()) {
            e.stopPropagation();
          }
          // Remove this event listener after first click
          document.removeEventListener('click', handleNextClick, true);
        };
        
        // Add a capture phase listener for the next click
        document.addEventListener('click', handleNextClick, true);
      }
    };
    
    document.addEventListener('mouseup', handleGlobalMouseUp);
    return () => {
      document.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, [expandedPost]);

  return (
    <div className="container py-6 max-w-7xl">
      <div className="flex flex-col space-y-6">
        {/* Header with title and action buttons */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight gradient-text">
              Instagram Content Calendar
            </h1>
            <p className="text-muted-foreground mt-1">
              Complete the 30-day Instagram challenge with AI-generated post ideas
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              className="gap-1.5"
              onClick={handleCreateTopic}
            >
              <Plus className="h-4 w-4" />
              Add Post Idea
            </Button>
            <Button
              onClick={generateSocialTopics}
              disabled={isGenerating}
              className="gap-1.5 bg-primary hover:bg-primary/90"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Instagram className="h-4 w-4" />
                  Generate Instagram Ideas
                </>
              )}
            </Button>
          </div>
        </div>

        {/* Generation progress section */}
        {isGenerating && (
          <Card className="mb-6 overflow-hidden border-zinc-200 dark:border-zinc-700">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-2">
                <div className="space-y-1">
                  <h3 className="font-medium text-zinc-900 dark:text-white text-lg">
                    Generating Instagram Content Ideas
                  </h3>
                  <p className="text-sm text-zinc-500 dark:text-zinc-400">
                    Our advanced AI system is creating high-engagement Instagram content tailored to your brand
                  </p>
                </div>
                <div className="text-sm text-blue-600 dark:text-blue-400 font-medium">
                  {Math.round(generationProgress)}%
                </div>
              </div>
              
              <Progress 
                value={generationProgress} 
                className="h-2 bg-blue-100 dark:bg-blue-950/30"
              />
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
                {[
                  { name: 'Brand Analysis Engine', icon: <Brain className="h-5 w-5" />, id: 'research', phase: 25 },
                  { name: 'Visual Strategy System', icon: <Instagram className="h-5 w-5" />, id: 'creative', phase: 50 },
                  { name: 'Neural Caption Generator', icon: <FileText className="h-5 w-5" />, id: 'writing', phase: 75 },
                  { name: 'Hashtag Optimization AI', icon: <Hash className="h-5 w-5" />, id: 'hashtag', phase: 100 }
                ].map((agent) => {
                  const isActive = generationProgress >= agent.phase - 25 && generationProgress < agent.phase;
                  const isComplete = generationProgress >= agent.phase;
                  
                  return (
                    <div 
                      key={agent.name}
                      className={cn(
                        "p-4 rounded-lg border transition-all duration-300",
                        isComplete 
                          ? "bg-green-50 dark:bg-green-900/20 border-green-100 dark:border-green-800/30" 
                          : isActive
                            ? "bg-blue-50 dark:bg-blue-900/20 border-blue-100 dark:border-blue-800/30"
                            : "bg-white dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700"
                      )}
                    >
                      <div className="text-sm font-medium flex items-center gap-2 mb-2">
                        <span className={cn(
                          "p-1.5 rounded-md",
                          isComplete 
                            ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400" 
                            : isActive
                              ? "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 animate-pulse"
                              : "bg-zinc-100 dark:bg-zinc-700 text-zinc-500 dark:text-zinc-400"
                        )}>
                          {isComplete ? <Check className="h-4 w-4" /> : agent.icon}
                        </span>
                        <span className="text-zinc-900 dark:text-white">{agent.name}</span>
                      </div>
                      
                      <p className={cn(
                        "text-xs transition-all duration-300 line-clamp-2 min-h-[2.5rem]",
                        isActive 
                          ? "text-blue-600 dark:text-blue-400" 
                          : "text-zinc-500 dark:text-zinc-400"
                      )}>
                        {agentDescriptions[agent.id as keyof typeof agentDescriptions]}
                      </p>
                    </div>
                  );
                })}
              </div>
            </CardContent>
            <div className="bg-zinc-50 dark:bg-zinc-900 border-t border-zinc-200 dark:border-zinc-700 p-3 text-center">
              <p className="text-xs text-zinc-500 dark:text-zinc-400">
                This process typically takes 15-20 seconds to complete
              </p>
            </div>
          </Card>
        )}

        {/* Main content area */}
        <div>
            {topics.length === 0 && !isLoading ? (
              <EmptySocialTopics onGenerateIdeas={generateSocialTopics} />
            ) : (
              <Card>
                <CardContent className="p-6">
                  <div className="flex flex-col space-y-6">
                    {/* Calendar Header */}
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-xl font-semibold text-zinc-900 dark:text-white">
                          {showArchived ? "Posted Instagram Content" : "30-Day Instagram Challenge"}
                        </h2>
                        <p className="text-sm text-zinc-500 dark:text-zinc-400">
                          {showArchived 
                            ? `${topics.filter(t => t.isPosted).length} posts added to your calendar` 
                            : "Post consistently for 30 days to grow your Instagram presence"}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="gap-1.5"
                          onClick={() => setShowArchived(!showArchived)}
                        >
                          {showArchived ? (
                            <>
                              <Calendar className="h-4 w-4" />
                              View Calendar
                            </>
                          ) : (
                            <>
                              <CheckCircle className="h-4 w-4" />
                              View Posted
                            </>
                          )}
                        </Button>
                      </div>
                    </div>
                    
                    {/* Challenge Progress Summary */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="bg-gradient-to-br from-blue-50 to-blue-50 dark:from-blue-950/30 dark:to-blue-950/30 rounded-lg p-4 border border-blue-100 dark:border-blue-900/50">
                        <div className="flex items-center gap-3">
                          <div className="bg-blue-100 dark:bg-blue-900/50 rounded-full p-2">
                            <CalendarIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                          </div>
                          <div>
                            <h3 className="text-sm font-medium text-zinc-900 dark:text-white">Challenge Progress</h3>
                            <p className="text-xs text-zinc-500 dark:text-zinc-400">
                              {topics.filter(t => t.isPosted).length} of 30 days completed
                            </p>
                          </div>
                        </div>
                        <div className="w-full bg-white dark:bg-zinc-800 rounded-full h-2 mt-3 overflow-hidden">
                          <div 
                            className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full transition-all duration-500 ease-out"
                            style={{ width: `${(topics.filter(t => t.isPosted).length / 30) * 100}%` }}
                          ></div>
                        </div>
                      </div>
                      
                      <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-950/30 dark:to-orange-950/30 rounded-lg p-4 border border-amber-100 dark:border-amber-900/50">
                        <div className="flex items-center gap-3">
                          <div className="bg-amber-100 dark:bg-amber-900/50 rounded-full p-2">
                            <Trophy className="h-5 w-5 text-amber-600 dark:text-amber-400" />
                          </div>
                          <div>
                            <h3 className="text-sm font-medium text-zinc-900 dark:text-white">Streak</h3>
                            <p className="text-xs text-zinc-500 dark:text-zinc-400">
                              {topics.filter(t => t.isPosted).length > 0 ? `${topics.filter(t => t.isPosted).length} day streak` : 'Start your streak today!'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between mt-3">
                          <div className="text-2xl font-bold text-amber-600 dark:text-amber-400">
                            {topics.filter(t => t.isPosted).length}
                          </div>
                          <div className="text-xs text-zinc-500 dark:text-zinc-400">
                            {30 - topics.filter(t => t.isPosted).length} days to go
                          </div>
                        </div>
                      </div>
                      
                      <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/30 dark:to-emerald-950/30 rounded-lg p-4 border border-green-100 dark:border-green-900/50">
                        <div className="flex items-center gap-3">
                          <div className="bg-green-100 dark:bg-green-900/50 rounded-full p-2">
                            <BarChart className="h-5 w-5 text-green-600 dark:text-green-400" />
                          </div>
                          <div>
                            <h3 className="text-sm font-medium text-zinc-900 dark:text-white">Content Mix</h3>
                            <p className="text-xs text-zinc-500 dark:text-zinc-400">
                              {uniqueTypes.length} different content types
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 mt-3 flex-wrap">
                          {uniqueTypes.slice(0, 5).map(type => (
                            <div 
                              key={type}
                              className={cn(
                                "text-xs px-2 py-0.5 rounded-full",
                              type === 'carousel' ? "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400 border border-amber-200/50 dark:border-amber-800/30" :
                              type === 'video' ? "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border border-red-200/50 dark:border-red-800/30" :
                              type === 'story' ? "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border border-blue-200/50 dark:border-blue-800/30" :
                              type === 'reel' ? "bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-400 border border-pink-200/50 dark:border-pink-800/30" :
                              type === 'infographic' ? "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border border-blue-200/50 dark:border-blue-800/30" :
                              type === 'single-image' ? "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-800/30" :
                              type === 'quote' ? "bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 border border-blue-200 dark:border-blue-800/30" :
                              type === 'user-generated' ? "bg-teal-100 dark:bg-teal-900/30 text-teal-700 dark:text-teal-400 border border-teal-200 dark:border-teal-800/30" :
                              type === 'behind-the-scenes' ? "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800/30" :
                              type === 'tutorial' ? "bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-400 border border-cyan-200 dark:border-cyan-800/30" :
                              "bg-zinc-100 dark:bg-zinc-800 text-zinc-700 dark:text-zinc-300 border border-zinc-200 dark:border-zinc-700/50"
                              )}
                            >
                              {type.charAt(0).toUpperCase() + type.slice(1).replace(/-/g, ' ')}
                            </div>
                          ))}
                          {uniqueTypes.length > 5 && (
                            <div className="text-xs text-zinc-500 dark:text-zinc-400">
                              +{uniqueTypes.length - 5} more
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {/* Calendar Grid */}
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                      {showArchived ? (
                        // Show posted content
                        filteredTopics.map((topic, index) => {
                          const isExpanded = expandedPost === topic?.id;
                          const dayNumber = topic?.metadata?.challengePosition || 0;
                          const currentTopic = topic;
                          
                          return (
                            <motion.div 
                              key={topic.id}
                              layout="position"
                              initial={{ opacity: 0, y: 20 }}
                              animate={{ opacity: 1, y: 0 }}
                              transition={{ 
                                duration: 0.3, 
                                delay: index * 0.02
                              }}
                              className={cn(
                                "relative rounded-lg border overflow-hidden transition-all duration-200",
                                isExpanded ? "md:col-span-2 lg:col-span-3 md:row-span-2 shadow-lg z-10" : "",
                                "border-green-200 dark:border-green-800 shadow-sm hover:shadow-md cursor-pointer"
                              )}
                              onClick={() => handleTopicClick(topic)}
                            >
                              <div className="p-4 flex flex-col h-full">
                                <div className="flex justify-between items-start mb-3">
                                  <div className="flex items-center">
                                    <span className={cn(
                                      "text-2xl font-bold mr-2",
                                      "text-zinc-900 dark:text-zinc-100"
                                    )}>
                                      {`Day ${currentTopic?.metadata?.challengePosition || dayNumber}`}
                                    </span>
                                    <Badge 
                                      className={cn(
                                        "px-2 py-0.5 text-xs",
                                        currentTopic?.type === 'carousel' ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 border border-amber-200 dark:border-amber-800/30" :
                                        currentTopic?.type === 'video' ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 border border-red-200 dark:border-red-800/30" :
                                        currentTopic?.type === 'story' ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border border-blue-200 dark:border-blue-800/30" :
                                        currentTopic?.type === 'reel' ? "bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300 border border-pink-200 dark:border-pink-800/30" :
                                        currentTopic?.type === 'infographic' ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border border-blue-200 dark:border-blue-800/30" :
                                        currentTopic?.type === 'single-image' ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800/30" :
                                        currentTopic?.type === 'quote' ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border border-blue-200 dark:border-blue-800/30" :
                                        currentTopic?.type === 'user-generated' ? "bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300 border border-teal-200 dark:border-teal-800/30" :
                                        currentTopic?.type === 'behind-the-scenes' ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 border border-red-200 dark:border-red-800/30" :
                                        currentTopic?.type === 'tutorial' ? "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300 border border-cyan-200 dark:border-cyan-800/30" :
                                        "bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300 border border-zinc-200 dark:border-zinc-700"
                                      )}
                                    >
                                      {currentTopic?.type.charAt(0).toUpperCase() + currentTopic?.type.slice(1).replace(/-/g, ' ')}
                                    </Badge>

                                    {topic?.isPosted && (
                                      <Badge 
                                        className="ml-2 px-2 py-0.5 text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 border border-green-200 dark:border-green-800/30"
                                      >
                                        Posted
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                                
                                {/* Visual description - always show a preview */}
                                <p 
                                  className="text-sm text-zinc-600 dark:text-zinc-400 mb-2 line-clamp-none"
                                  onClick={(e) => handleTextInteraction(e, currentTopic?.id || '')}
                                  onMouseDown={(e) => handleSelectionStart(e, currentTopic?.id || '')}
                                  onMouseUp={(e) => handleTextInteraction(e, currentTopic?.id || '')}
                                  onMouseMove={(e) => handleTextInteraction(e, currentTopic?.id || '')}
                                  onTouchStart={(e) => handleTextInteraction(e, currentTopic?.id || '')}
                                  onTouchMove={(e) => handleTextInteraction(e, currentTopic?.id || '')}
                                  onTouchEnd={(e) => handleTextInteraction(e, currentTopic?.id || '')}
                                >
                                  {currentTopic?.visualDescription}
                                </p>
                                
                                {/* Show more content when expanded */}
                                {isExpanded && (
                                  <div className="mt-4 space-y-4">
                                    <div>
                                      <div className="flex items-center justify-between mb-1">
                                        <h4 className="text-sm font-medium">Caption</h4>
                                        <Button
                                          variant="ghost"
                                          size="icon"
                                          className="h-6 w-6 text-zinc-500 hover:text-blue-600 dark:text-zinc-400 dark:hover:text-blue-400"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            if (currentTopic?.caption) {
                                              const textToCopy = currentTopic.hashtags 
                                                ? `${currentTopic.caption}\n\n${currentTopic.hashtags}` 
                                                : currentTopic.caption;
                                              navigator.clipboard.writeText(textToCopy);
                                              toast({
                                                title: "Copied!",
                                                description: "Caption and hashtags copied to clipboard",
                                                variant: "success",
                                              });
                                            }
                                          }}
                                        >
                                          <Copy className="h-3.5 w-3.5" />
                                        </Button>
                                      </div>
                                      <p 
                                        className="text-sm text-zinc-600 dark:text-zinc-400 whitespace-pre-wrap"
                                        onClick={(e) => handleTextInteraction(e, currentTopic?.id || '')}
                                        onMouseDown={(e) => handleSelectionStart(e, currentTopic?.id || '')}
                                        onMouseUp={(e) => handleTextInteraction(e, currentTopic?.id || '')}
                                        onMouseMove={(e) => handleTextInteraction(e, currentTopic?.id || '')}
                                        onTouchStart={(e) => handleTextInteraction(e, currentTopic?.id || '')}
                                        onTouchMove={(e) => handleTextInteraction(e, currentTopic?.id || '')}
                                        onTouchEnd={(e) => handleTextInteraction(e, currentTopic?.id || '')}
                                      >
                                        {currentTopic?.caption}
                                      </p>
                                    </div>
                                    
                                    {currentTopic?.hashtags && (
                                      <div>
                                        <div className="flex items-center justify-between mb-1">
                                          <h4 className="text-sm font-medium">Hashtags</h4>
                                        </div>
                                        <div 
                                          className="flex flex-wrap gap-1"
                                          onClick={(e) => handleTextInteraction(e, currentTopic?.id || '')}
                                          onMouseDown={(e) => handleSelectionStart(e, currentTopic?.id || '')}
                                          onMouseUp={(e) => handleTextInteraction(e, currentTopic?.id || '')}
                                          onMouseMove={(e) => handleTextInteraction(e, currentTopic?.id || '')}
                                          onTouchStart={(e) => handleTextInteraction(e, currentTopic?.id || '')}
                                          onTouchMove={(e) => handleTextInteraction(e, currentTopic?.id || '')}
                                          onTouchEnd={(e) => handleTextInteraction(e, currentTopic?.id || '')}
                                        >
                                          {typeof currentTopic.hashtags === 'string' && 
                                            currentTopic.hashtags.split(' ').map((tag, i) => (
                                              <Badge key={i} variant="secondary" className="text-xs">
                                                {tag}
                                              </Badge>
                                            ))
                                          }
                                        </div>
                                      </div>
                                    )}
                                    
                                    <div className="flex justify-between pt-2">
                                      {currentTopic?.isPosted ? (
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          className="gap-1.5 text-red-600"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            togglePosted(currentTopic.id);
                                          }}
                                        >
                                          <X className="h-4 w-4" />
                                          Unpost
                                        </Button>
                                      ) : (
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          className="gap-1.5"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            togglePosted(currentTopic.id);
                                          }}
                                        >
                                          <Calendar className="h-4 w-4" />
                                          Mark as Posted
                                        </Button>
                                      )}
                                      
                                      <div className="flex gap-2">
                                        <Button
                                          variant="outline"
                                          size="sm"
                                          className="gap-1.5 text-red-600"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            deleteTopic(currentTopic.id);
                                            setExpandedPost(null);
                                          }}
                                        >
                                          <Trash2 className="h-4 w-4" />
                                          Delete
                                        </Button>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </div>
                            </motion.div>
                          );
                        })
                      ) : (
                        // Show the 30-day calendar
                        Array.from({ length: 30 }).map((_, index) => {
                          const dayNumber = index + 1;
                          // Find a post that is marked as posted and has this day number as its challenge position
                          const postForDay = topics.find(t => 
                            t?.isPosted && 
                            t?.metadata?.challengePosition === dayNumber
                          );
                          
                          // For available (unposted) posts, we'll show them according to their position in the array
                          let availablePost = null;
                          
                          if (!postForDay) {
                            // Filter unposted topics
                            const unpostedTopics = topics.filter(t => !t?.isPosted);
                            
                            // Only assign if we have enough unposted topics to reach this day
                            if (unpostedTopics.length > 0) {
                              // Use index for sequential assignment, but avoid going out of bounds
                              const availableIndex = index % unpostedTopics.length;
                              availablePost = unpostedTopics[availableIndex];
                            }
                          }
                          
                          const isExpanded = expandedPost === (postForDay?.id || availablePost?.id);
                          const currentTopic = postForDay || availablePost;
                          
                          return (
                            <div 
                              key={index}
                              className={cn(
                                "relative rounded-lg border overflow-hidden transition-all duration-200",
                                isExpanded ? "md:col-span-2 lg:col-span-3 row-span-2 shadow-lg" : "",
                                postForDay 
                                  ? "border-zinc-300 dark:border-zinc-600 shadow-sm hover:shadow-md cursor-pointer bg-zinc-100 dark:bg-zinc-800/70" 
                                  : availablePost
                                    ? "border-blue-200 dark:border-blue-800 shadow-sm hover:shadow-md cursor-pointer"
                                    : "border-zinc-200 dark:border-zinc-700 bg-zinc-50 dark:bg-zinc-800/50"
                              )}
                              onClick={() => {
                                if (currentTopic) {
                                  handleTopicClick(currentTopic);
                                }
                              }}
                            >
                              {/* Only render the expanded content with motion for the expanded post */}
                              {isExpanded && currentTopic ? (
                                <motion.div
                                  layout="position"
                                  initial={false}
                                  animate={{ opacity: 1 }}
                                  className="h-full"
                                >
                                  <div className="p-4 flex flex-col h-full">
                                    <div className="flex justify-between items-start mb-3">
                                      <div className="flex items-center">
                                        <span className={cn(
                                          "text-2xl font-bold mr-2",
                                          "text-zinc-900 dark:text-zinc-100"
                                        )}>
                                          {`Day ${currentTopic?.metadata?.challengePosition || dayNumber}`}
                                        </span>
                                        <Badge 
                                          className={cn(
                                            "px-2 py-0.5 text-xs",
                                            currentTopic?.type === 'carousel' ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 border border-amber-200 dark:border-amber-800/30" :
                                            currentTopic?.type === 'video' ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 border border-red-200 dark:border-red-800/30" :
                                            currentTopic?.type === 'story' ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border border-blue-200 dark:border-blue-800/30" :
                                            currentTopic?.type === 'reel' ? "bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300 border border-pink-200 dark:border-pink-800/30" :
                                            currentTopic?.type === 'infographic' ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border border-blue-200 dark:border-blue-800/30" :
                                            currentTopic?.type === 'single-image' ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800/30" :
                                            currentTopic?.type === 'quote' ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border border-blue-200 dark:border-blue-800/30" :
                                            currentTopic?.type === 'user-generated' ? "bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300 border border-teal-200 dark:border-teal-800/30" :
                                            currentTopic?.type === 'behind-the-scenes' ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 border border-red-200 dark:border-red-800/30" :
                                            currentTopic?.type === 'tutorial' ? "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300 border border-cyan-200 dark:border-cyan-800/30" :
                                            "bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300 border border-zinc-200 dark:border-zinc-700"
                                          )}
                                        >
                                          {currentTopic?.type.charAt(0).toUpperCase() + currentTopic?.type.slice(1).replace(/-/g, ' ')}
                                        </Badge>

                                        {currentTopic?.isPosted && (
                                          <Badge 
                                            className="ml-2 px-2 py-0.5 text-xs bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300 border border-green-200 dark:border-green-800/30"
                                          >
                                            Posted
                                          </Badge>
                                        )}
                                      </div>
                                    </div>
                                    
                                    {/* Visual description - always show a preview */}
                                    <p 
                                      className="text-sm text-zinc-600 dark:text-zinc-400 mb-2 line-clamp-none"
                                      onClick={(e) => handleTextInteraction(e, currentTopic?.id || '')}
                                      onMouseDown={(e) => handleSelectionStart(e, currentTopic?.id || '')}
                                      onMouseUp={(e) => handleTextInteraction(e, currentTopic?.id || '')}
                                      onMouseMove={(e) => handleTextInteraction(e, currentTopic?.id || '')}
                                      onTouchStart={(e) => handleTextInteraction(e, currentTopic?.id || '')}
                                      onTouchMove={(e) => handleTextInteraction(e, currentTopic?.id || '')}
                                      onTouchEnd={(e) => handleTextInteraction(e, currentTopic?.id || '')}
                                    >
                                      {currentTopic?.visualDescription}
                                    </p>
                                    
                                    {/* Show more content when expanded */}
                                    <div className="mt-4 space-y-4">
                                      <div>
                                        <div className="flex items-center justify-between mb-1">
                                          <h4 className="text-sm font-medium">Caption</h4>
                                          <Button
                                            variant="ghost"
                                            size="icon"
                                            className="h-6 w-6 text-zinc-500 hover:text-blue-600 dark:text-zinc-400 dark:hover:text-blue-400"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              if (currentTopic?.caption) {
                                                const textToCopy = currentTopic.hashtags 
                                                  ? `${currentTopic.caption}\n\n${currentTopic.hashtags}` 
                                                  : currentTopic.caption;
                                                navigator.clipboard.writeText(textToCopy);
                                                toast({
                                                  title: "Copied!",
                                                  description: "Caption and hashtags copied to clipboard",
                                                  variant: "success",
                                                });
                                              }
                                            }}
                                          >
                                            <Copy className="h-3.5 w-3.5" />
                                          </Button>
                                        </div>
                                        <p 
                                          className="text-sm text-zinc-600 dark:text-zinc-400 whitespace-pre-wrap"
                                          onClick={(e) => handleTextInteraction(e, currentTopic?.id || '')}
                                          onMouseDown={(e) => handleSelectionStart(e, currentTopic?.id || '')}
                                          onMouseUp={(e) => handleTextInteraction(e, currentTopic?.id || '')}
                                          onMouseMove={(e) => handleTextInteraction(e, currentTopic?.id || '')}
                                          onTouchStart={(e) => handleTextInteraction(e, currentTopic?.id || '')}
                                          onTouchMove={(e) => handleTextInteraction(e, currentTopic?.id || '')}
                                          onTouchEnd={(e) => handleTextInteraction(e, currentTopic?.id || '')}
                                        >
                                          {currentTopic?.caption}
                                        </p>
                                      </div>
                                      
                                      {currentTopic?.hashtags && (
                                        <div>
                                          <div className="flex items-center justify-between mb-1">
                                            <h4 className="text-sm font-medium">Hashtags</h4>
                                          </div>
                                          <div 
                                            className="flex flex-wrap gap-1"
                                            onClick={(e) => handleTextInteraction(e, currentTopic?.id || '')}
                                            onMouseDown={(e) => handleSelectionStart(e, currentTopic?.id || '')}
                                            onMouseUp={(e) => handleTextInteraction(e, currentTopic?.id || '')}
                                            onMouseMove={(e) => handleTextInteraction(e, currentTopic?.id || '')}
                                            onTouchStart={(e) => handleTextInteraction(e, currentTopic?.id || '')}
                                            onTouchMove={(e) => handleTextInteraction(e, currentTopic?.id || '')}
                                            onTouchEnd={(e) => handleTextInteraction(e, currentTopic?.id || '')}
                                          >
                                            {typeof currentTopic.hashtags === 'string' && 
                                              currentTopic.hashtags.split(' ').map((tag, i) => (
                                                <Badge key={i} variant="secondary" className="text-xs">
                                                  {tag}
                                                </Badge>
                                              ))
                                            }
                                          </div>
                                        </div>
                                      )}
                                      
                                      <div className="flex justify-end pt-2">
                                        <div className="flex gap-2">
                                          <Button
                                            variant="outline"
                                            size="sm"
                                            className="gap-1.5 text-red-600"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              deleteTopic(currentTopic.id);
                                              setExpandedPost(null);
                                            }}
                                          >
                                            <Trash2 className="h-4 w-4" />
                                            Delete
                                          </Button>
                                          
                                          {currentTopic?.isPosted ? (
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              className="gap-1.5 text-blue-600"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                togglePosted(currentTopic.id);
                                              }}
                                            >
                                              <X className="h-4 w-4" />
                                              Unpost
                                            </Button>
                                          ) : (
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              className="gap-1.5 text-blue-600"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                togglePosted(currentTopic.id);
                                              }}
                                            >
                                              <Calendar className="h-4 w-4" />
                                              Mark as Posted
                                            </Button>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                </motion.div>
                              ) : currentTopic ? (
                                // Non-expanded content without motion
                                <div className="p-4 flex flex-col h-full">
                                  <div className="flex justify-between items-start mb-3">
                                    <div className="flex items-center">
                                      <span className={cn(
                                        "text-2xl font-bold mr-2",
                                        "text-zinc-900 dark:text-zinc-100"
                                      )}>
                                        {`Day ${currentTopic?.metadata?.challengePosition || dayNumber}`}
                                      </span>
                                      <Badge 
                                        className={cn(
                                          "px-2 py-0.5 text-xs",
                                          currentTopic?.type === 'carousel' ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 border border-amber-200 dark:border-amber-800/30" :
                                          currentTopic?.type === 'video' ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 border border-red-200 dark:border-red-800/30" :
                                          currentTopic?.type === 'story' ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border border-blue-200 dark:border-blue-800/30" :
                                          currentTopic?.type === 'reel' ? "bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300 border border-pink-200 dark:border-pink-800/30" :
                                          currentTopic?.type === 'infographic' ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border border-blue-200 dark:border-blue-800/30" :
                                          currentTopic?.type === 'single-image' ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800/30" :
                                          currentTopic?.type === 'quote' ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border border-blue-200 dark:border-blue-800/30" :
                                          currentTopic?.type === 'user-generated' ? "bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300 border border-teal-200 dark:border-teal-800/30" :
                                          currentTopic?.type === 'behind-the-scenes' ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 border border-red-200 dark:border-red-800/30" :
                                          currentTopic?.type === 'tutorial' ? "bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300 border border-cyan-200 dark:border-cyan-800/30" :
                                          "bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300 border border-zinc-200 dark:border-zinc-700"
                                        )}
                                      >
                                        {currentTopic?.type.charAt(0).toUpperCase() + currentTopic?.type.slice(1).replace(/-/g, ' ')}
                                      </Badge>
                                    </div>
                                  </div>
                                  
                                  {/* Visual description - always show a preview */}
                                  <p 
                                    className="text-sm text-zinc-600 dark:text-zinc-400 mb-2 line-clamp-3"
                                    onClick={(e) => handleTextInteraction(e, currentTopic?.id || '')}
                                    onMouseDown={(e) => handleSelectionStart(e, currentTopic?.id || '')}
                                    onMouseUp={(e) => handleTextInteraction(e, currentTopic?.id || '')}
                                    onMouseMove={(e) => handleTextInteraction(e, currentTopic?.id || '')}
                                    onTouchStart={(e) => handleTextInteraction(e, currentTopic?.id || '')}
                                    onTouchMove={(e) => handleTextInteraction(e, currentTopic?.id || '')}
                                    onTouchEnd={(e) => handleTextInteraction(e, currentTopic?.id || '')}
                                  >
                                    {currentTopic?.visualDescription}
                                  </p>
                                </div>
                              ) : (
                                <div className="p-4 text-center text-zinc-500">
                                  <div className={cn(
                                    "w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium mx-auto mb-2",
                                    "bg-zinc-100 dark:bg-zinc-700 text-zinc-700 dark:text-zinc-300"
                                  )}>
                                    {dayNumber}
                                  </div>
                                  <span className="text-xs">Empty slot</span>
                                </div>
                              )}
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
        </div>
      </div>

      {/* New Topic Dialog */}
      <Dialog open={showNewTopicDialog} onOpenChange={setShowNewTopicDialog}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Create New Instagram Post Idea</DialogTitle>
            <DialogDescription>
              Add a new Instagram post idea to your content library
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <label htmlFor="visual-description" className="text-sm font-medium">
                Visual Description
              </label>
              <Textarea
                id="visual-description"
                placeholder="Describe what the image or video should look like..."
                value={newTopic.visualDescription}
                onChange={(e) => setNewTopic({ ...newTopic, visualDescription: e.target.value })}
                className="min-h-[80px]"
              />
            </div>
            <div className="grid gap-2">
              <label htmlFor="caption" className="text-sm font-medium">
                Caption
              </label>
              <Textarea
                id="caption"
                placeholder="Write your Instagram caption here..."
                value={newTopic.caption}
                onChange={(e) => setNewTopic({ ...newTopic, caption: e.target.value })}
                className="min-h-[100px]"
              />
            </div>
            <div className="grid gap-2">
              <label htmlFor="hashtags" className="text-sm font-medium">
                Hashtags
              </label>
              <Input
                id="hashtags"
                placeholder="#marketing #socialmedia #content"
                value={newTopic.hashtags}
                onChange={(e) => setNewTopic({ ...newTopic, hashtags: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <label htmlFor="type" className="text-sm font-medium">
                  Content Type
                </label>
                <Select
                  value={newTopic.type}
                  onValueChange={(value) => setNewTopic({ ...newTopic, type: value })}
                >
                  <SelectTrigger id="type">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="carousel">Carousel</SelectItem>
                    <SelectItem value="single-image">Single Image</SelectItem>
                    <SelectItem value="video">Video</SelectItem>
                    <SelectItem value="story">Story</SelectItem>
                    <SelectItem value="reel">Reel</SelectItem>
                    <SelectItem value="quote">Quote</SelectItem>
                    <SelectItem value="infographic">Infographic</SelectItem>
                    <SelectItem value="user-generated">User Generated</SelectItem>
                    <SelectItem value="behind-the-scenes">Behind the Scenes</SelectItem>
                    <SelectItem value="tutorial">Tutorial</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid gap-2">
                <label htmlFor="audience" className="text-sm font-medium">
                  Target Audience
                </label>
                <Select
                  value={newTopic.audience}
                  onValueChange={(value) => setNewTopic({ ...newTopic, audience: value })}
                >
                  <SelectTrigger id="audience">
                    <SelectValue placeholder="Select audience" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="marketing-managers">Marketing Managers</SelectItem>
                    <SelectItem value="business-owners">Business Owners</SelectItem>
                    <SelectItem value="startup-founders">Startup Founders</SelectItem>
                    <SelectItem value="product-managers">Product Managers</SelectItem>
                    <SelectItem value="technical-developers">Technical Developers</SelectItem>
                    <SelectItem value="general">General Audience</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowNewTopicDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={submitNewTopic} 
              disabled={isCreating || !newTopic.visualDescription || !newTopic.caption}
              className="gap-1.5 bg-primary hover:bg-primary/90"
            >
              {isCreating ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4" />
                  Create Post Idea
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add the topic detail dialog */}
      <Dialog open={showTopicDetailDialog} onOpenChange={setShowTopicDetailDialog}>
        <DialogContent className="sm:max-w-[700px] p-0 overflow-hidden">
          {selectedTopic ? (
            <div className="flex flex-col gap-4 p-6">
              <div className="flex items-center gap-2">
                <Badge 
                  variant="outline" 
                  className={cn(
                    "text-xs font-medium",
                    selectedTopic?.type === 'carousel' && "bg-blue-100 text-blue-800 hover:bg-blue-100",
                    selectedTopic?.type === 'video' && "bg-blue-100 text-blue-800 hover:bg-blue-100",
                    selectedTopic?.type === 'story' && "bg-pink-100 text-pink-800 hover:bg-pink-100",
                    selectedTopic?.type === 'reel' && "bg-orange-100 text-orange-800 hover:bg-orange-100",
                    selectedTopic?.type === 'infographic' && "bg-green-100 text-green-800 hover:bg-green-100",
                    selectedTopic?.type === 'single-image' && "bg-yellow-100 text-yellow-800 hover:bg-yellow-100",
                    selectedTopic?.type === 'quote' && "bg-blue-100 text-blue-800 hover:bg-blue-100",
                    selectedTopic?.type === 'user-generated' && "bg-teal-100 text-teal-800 hover:bg-teal-100",
                    selectedTopic?.type === 'behind-the-scenes' && "bg-red-100 text-red-800 hover:bg-red-100",
                    selectedTopic?.type === 'tutorial' && "bg-cyan-100 text-cyan-800 hover:bg-cyan-100",
                  )}
                >
                  {selectedTopic?.type}
                </Badge>
                {selectedTopic?.isPosted && (
                  <Badge variant="outline" className="bg-green-100 text-green-800 hover:bg-green-100">
                    Posted
                  </Badge>
                )}
              </div>
              
              <div className="space-y-2">
                <h3 className="text-lg font-semibold">Visual Description</h3>
                <p className="text-sm text-gray-600">{selectedTopic?.visualDescription || "No visual description available"}</p>
              </div>
              
              <div className="space-y-2">
                <h3 className="text-lg font-semibold">Caption</h3>
                <p className="text-sm text-gray-600">{selectedTopic?.caption || "No caption available"}</p>
              </div>
              
              <div className="space-y-2">
                <h3 className="text-lg font-semibold">Hashtags</h3>
                <div className="flex flex-wrap gap-2">
                  {selectedTopic?.hashtags ? (
                    selectedTopic.hashtags.split(' ').map((tag, index) => (
                      <Badge key={index} variant="secondary" className="text-xs">
                        #{tag}
                      </Badge>
                    ))
                  ) : (
                    <p className="text-sm text-gray-600">No hashtags available</p>
                  )}
                </div>
              </div>
              
              <div className="space-y-2">
                <h3 className="text-lg font-semibold">Target Audience</h3>
                <p className="text-sm text-gray-600">{selectedTopic?.audience || "No target audience specified"}</p>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center p-6">
              <p className="text-gray-500">No topic selected</p>
            </div>
          )}

          {/* Footer Actions */}
          {selectedTopic && (
            <div className="border-t border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-900 p-4 flex justify-between items-center">
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-9 px-4 gap-1.5 text-red-600 border-red-200 hover:bg-red-50 dark:text-red-400 dark:border-red-800 dark:hover:bg-red-900/20"
                  onClick={() => {
                    if (selectedTopic?.id) {
                      deleteTopic(selectedTopic.id);
                      setShowTopicDetailDialog(false);
                    }
                  }}
                >
                  <Trash2 className="h-4 w-4" />
                  Delete
                </Button>
              </div>
              <div>
                <Button
                  size="sm"
                  className={cn(
                    "h-9 px-4 gap-1.5",
                    selectedTopic?.isPosted 
                      ? "bg-white hover:bg-gray-100 text-red-600 border border-red-200" 
                      : "bg-primary hover:bg-primary/90 text-primary-foreground"
                  )}
                  onClick={() => {
                    if (selectedTopic?.id) {
                      togglePosted(selectedTopic.id);
                      setShowTopicDetailDialog(false);
                    }
                  }}
                >
                  {selectedTopic?.isPosted ? (
                    <>
                      <X className="h-4 w-4" />
                      Unpost
                    </>
                  ) : (
                    <>
                      <Calendar className="h-4 w-4" />
                      Add to Calendar
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SocialTopicsPage; 